import { Volume } from '~/shared/types/api';

/**
 * Stable identity for AG Grid rows and React keys. Prefer API `id` (UUID); fall back to `volumeId`.
 */
export const getVolumeStableRowId = (v: Pick<Volume, 'id' | 'volumeId'>): string =>
  (v.id && v.id.length > 0 ? v.id : v.volumeId) || 'unknown';

/**
 * Check if a volume can be datafied (turned on)
 */
export const canBeDatafied = (v: Volume) => v.isDatafiable;

/**
 * Check if a volume can be undatafied (turned off)
 * A volume can be undatafied when:
 * - The backend flag isUnDatafiable is true
 * - AND the volume is in one of the allowed monitoring states
 */
export const canBeUnDatafied = (v: Volume) =>
  v.isUnDatafiable && ['Managed', 'Migrating', 'Shrinking'].includes(v?.monitoring || '');

/**
 * Check if a volume's datafy switch is currently in the "on" state
 * This includes all managed states and transitional states.
 *
 * Accepts any object exposing `monitoring` so summary payloads (e.g. dry-run
 * `VolumeSummary`) work alongside full `Volume` objects.
 */
export const isSwitchedOn = (v: { monitoring?: Volume['monitoring'] }): boolean =>
  ['Migrating', 'Growing', 'Shrinking', 'Merging', 'Managed'].includes(v?.monitoring || '');

/**
 * Check if the datafy switch should be disabled
 * The switch is disabled when:
 * - The volume cannot be datafied AND cannot be undatafied
 * - OR the volume is switched on but cannot be undatafied
 */
export const isSwitchDisabled = (v: Volume): boolean =>
  (!canBeDatafied(v) && !canBeUnDatafied(v)) || (isSwitchedOn(v) && !canBeUnDatafied(v));
